<template>
  <!-- Autocomplete produit : la liste complète (issue du store) n'est jamais
       rendue d'un coup. On filtre en flou (maison, sans dépendance) à la frappe
       et on plafonne les résultats affichés (cf. LIMIT). -->
  <v-select
    :value="selectedOption"
    :options="results"
    :filterable="false"
    label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    class="product-ac"
    @input="onInput"
    @search="onSearch"
  >
    <!-- Format « CODE - Désignation », en texte simple : identique au reste des
         listes déroulantes produit du back-office. -->
    <template slot="option" slot-scope="opt">
      <div class="product-ac__opt">{{ opt.label }}</div>
    </template>

    <template slot="selected-option" slot-scope="opt">
      <span class="product-ac__label">{{ opt.label }}</span>
    </template>

    <template slot="no-options" slot-scope="{ search }">
      <div class="product-ac__empty">
        <template v-if="normalize(search).trim().length < minChars">
          <i class="far fa-keyboard"></i> Tapez au moins {{ minChars }} caractères…
        </template>
        <template v-else>Aucun produit pour «&nbsp;{{ search }}&nbsp;»</template>
      </div>
    </template>

    <li v-if="hiddenCount > 0" slot="list-footer" class="product-ac__more">
      <i class="far fa-ellipsis-h"></i> {{ hiddenCount }} autre(s) résultat(s) — précisez la recherche
    </li>
  </v-select>
</template>

<script>
// Nombre max de résultats rendus dans le menu (on « fait sauter » le reste).
const LIMIT = 30;
// Longueur minimale de recherche avant de calculer les résultats.
const MIN_CHARS = 2;

// Minuscule + suppression des accents, pour un matching tolérant.
function normalize(s) {
  return (s == null ? "" : String(s))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // marques diacritiques combinantes
}

// Délai avant d'interroger le serveur, en mode `remote`.
const DEBOUNCE = 300;

export default {
  name: "ProductAutocomplete",
  props: {
    value: { default: null },
    placeholder: { type: String, default: "Rechercher un produit…" },
    /**
     * Interroge le serveur à la frappe au lieu de compter sur un store préchargé.
     * Le catalogue dépasse 4 000 produits : les écrans qui n'ont pas déjà besoin de la
     * liste complète ne la chargent pas (AC-002).
     */
    remote: { type: Boolean, default: false },
    /** Restreint la recherche à un statut produit (ex. 'product_on_sale'). */
    statusSlug: { type: String, default: null },
    /**
     * Restreint la recherche à une nature d'offre (champ Type de la fiche produit :
     * 'selling', 'renting', 'location_for_account'). Le store est partagé entre les
     * écrans : le filtre est donc appliqué localement en plus d'être transmis au serveur.
     */
    offerType: { type: String, default: null },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { results: [], hiddenCount: 0, minChars: MIN_CHARS, timer: null };
  },
  computed: {
    products() {
      return this.$store.getters["product/getAll"] || [];
    },
    // Index normalisé, recalculé seulement si la liste du store change.
    index() {
      return this.products.map((p) => {
        const code = p.internal_code || "";
        const name = p.name || "";
        return {
          value: p.id,
          code,
          name,
          status_slug: p.status_slug,
          type: p.type,
          label: (code ? code + " - " : "") + name,
          hay: normalize(code + " " + name),
          codeHay: normalize(code),
        };
      });
    },
    /**
     * Ce parmi quoi on cherche. Distinct de `index` : le store peut contenir des produits
     * chargés pour une autre raison (la sélection en cours, y compris devenue indisponible),
     * qui ne doivent pas être proposés — mais dont le libellé doit rester résoluble.
     */
    searchIndex() {
      if (!this.statusSlug && !this.offerType) {
        return this.index;
      }
      return this.index.filter(
        (o) =>
          (!this.statusSlug || o.status_slug === this.statusSlug) &&
          (!this.offerType || o.type === this.offerType)
      );
    },
    // L'option sélectionnée est cherchée dans l'index complet (pas dans
    // `results`), pour que le libellé s'affiche même hors résultats filtrés.
    selectedOption() {
      if (this.value == null) return null;
      return this.index.find((o) => o.value === this.value) || null;
    },
  },
  methods: {
    normalize,
    onInput(option) {
      this.$emit("input", option ? option.value : null);
    },
    onSearch(search, loading) {
      const q = normalize(search).trim();
      if (q.length < MIN_CHARS) {
        this.results = [];
        this.hiddenCount = 0;
        return;
      }

      if (!this.remote) {
        this.computeResults(q);
        return;
      }

      // Le serveur alimente le store ; le classement reste local, identique aux deux modes.
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (loading) loading(true);
        this.$store
          .dispatch("product/getAllForSelectBySearch", {
            wait: true,
            search: search,
            statusSlug: this.statusSlug,
            offerType: this.offerType,
            add: true,
          })
          .then(() => this.computeResults(q))
          .catch(() => this.computeResults(q))
          .then(() => {
            if (loading) loading(false);
          });
      }, DEBOUNCE);
    },
    computeResults(q) {
      const tokens = q.split(/\s+/);
      const scored = [];
      for (const item of this.searchIndex) {
        const s = this.scoreItem(item, tokens);
        if (s > 0) scored.push({ item, s });
      }
      scored.sort((a, b) => b.s - a.s || a.item.label.localeCompare(b.item.label));
      this.hiddenCount = Math.max(0, scored.length - LIMIT);
      this.results = scored.slice(0, LIMIT).map((x) => x.item);
    },
    // Tous les mots de la requête doivent matcher ; le score total classe.
    scoreItem(item, tokens) {
      let total = 0;
      for (const t of tokens) {
        const s = this.scoreToken(item, t);
        if (s <= 0) return 0;
        total += s;
      }
      return total;
    },
    // Priorité : code exact/préfixe > sous-chaîne (plus tôt = mieux) > sous-séquence.
    scoreToken(item, t) {
      if (item.codeHay === t) return 200;
      if (item.codeHay.indexOf(t) === 0) return 120;
      const idx = item.hay.indexOf(t);
      if (idx === 0) return 100;
      if (idx > 0) return 60 - Math.min(idx, 30);
      return this.isSubsequence(item.hay, t) ? 15 : 0;
    },
    isSubsequence(hay, t) {
      let h = 0;
      for (let i = 0; i < t.length; i++) {
        h = hay.indexOf(t[i], h);
        if (h === -1) return false;
        h++;
      }
      return true;
    },
  },
};
</script>

<style scoped>
/* Racine bornée à la largeur de la colonne, sinon le toggle grandit avec le
   contenu et la valeur déborde du bloc (l'ellipsis ne peut pas se déclencher). */
.product-ac {
  width: 100%;
}
.product-ac >>> .vs__dropdown-toggle {
  background: #fff;
  border-color: #e5e6e7;
  border-radius: 3px;
  min-height: 31px;
  max-width: 100%;
  padding: 0; /* défaut asymétrique 0 0 4px → casse le centrage */
  align-items: stretch;
}
/* En single-select fermé, .vs__selected est un flex-item avec margin-top:4px.
   On centre verticalement en supprimant les marges et via align-items.
   min-width:0 + overflow permettent à l'enfant de rétrécir (→ ellipsis). */
.product-ac >>> .vs__selected-options {
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  padding: 0 2px;
}
.product-ac >>> .vs__selected {
  font-size: 13px;
  margin: 0 2px;
  max-width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
}
/* Valeur sélectionnée : forcée sur une seule ligne, tronquée par « … ». */
.product-ac >>> .vs__selected .product-ac__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-ac >>> .vs__search {
  font-size: 13px;
  margin: 0;
}
.product-ac >>> .vs__actions {
  padding-top: 0;
}
.product-ac >>> .vs__dropdown-menu {
  padding: 4px 0;
}
.product-ac >>> .vs__dropdown-option {
  padding: 6px 12px;
}
.product-ac >>> .vs__dropdown-option--highlight {
  background: #1ab394;
  color: #fff;
}
.product-ac__opt {
  white-space: normal;
  color: #34495e;
  font-size: 13px;
}
.product-ac >>> .vs__dropdown-option--highlight .product-ac__opt {
  color: #fff;
}
.product-ac__label {
  color: #34495e;
  font-size: 13px;
}
.product-ac__empty {
  padding: 6px 4px;
  color: #909499;
  font-size: 13px;
}
.product-ac__more {
  list-style: none;
  padding: 6px 12px;
  font-size: 12px;
  color: #909499;
  border-top: 1px solid #f1f3f5;
  background: #fafbfc;
}
.product-ac__more i {
  margin-right: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/ProductAutocomplete.vue